import { Alert, Box, Button } from '@mui/material';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { authService } from '@shared/services/AuthService';
import { useSnackbar } from '@shared/services/SnackbarService/SnackbarServiceContext';
import { useState } from 'react';
import Input from '@/shared/components/Input';

function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const passwordsMatch = newPassword === confirmPassword;
  const isFormValid = currentPassword && newPassword && confirmPassword && passwordsMatch;

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!isFormValid) {
      showSnackbar('Please ensure all fields are filled and passwords match.', 'error');
      return;
    }

    try {
      setLoading(true);
      const currentUser = await authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      await authService.authenticateUser(currentUser.email, currentPassword);
      await authService.changePassword(currentPassword, newPassword);

      showSnackbar('Your password has been successfully changed!', 'success');

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      showSnackbar('There was an error changing your password. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="change-password-loading">
        <LoadingSpinner variant="linear" size="medium" />
      </div>
    );
  }

  return (
    <Box component="form" onSubmit={onSubmit} className="change-password-form">
      <Alert severity="info" className="change-password-alert">
        Choose a strong password with at least 8 characters, including uppercase, lowercase, numbers, and special
        characters.
      </Alert>

      <div className="change-password-field">
        <Input
          name="currentPassword"
          label="Current Password"
          type="password"
          currentValue={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          fullWidth
        />
      </div>

      <div className="change-password-field">
        <Input
          name="newPassword"
          label="New Password"
          type="password"
          currentValue={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          fullWidth
        />
      </div>

      <div className="change-password-field">
        <Input
          name="confirmPassword"
          label="Confirm New Password"
          type="password"
          currentValue={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          fullWidth
          error={!!(confirmPassword && !passwordsMatch)}
          helperText={confirmPassword && !passwordsMatch ? 'Passwords do not match' : undefined}
        />
      </div>

      <div className="change-password-button-container">
        <Button type="submit" variant="contained" disabled={!isFormValid || loading} className="change-password-button">
          Update Password
        </Button>
      </div>
    </Box>
  );
}

export default ChangePassword;
